import * as React from 'react';

/**
 * Shadow DOM Context Value
 * Provides shadow root and portal container to components within a Shadow DOM
 */
export interface ShadowDOMContextValue {
  /** The ShadowRoot instance, or null if not in a shadow DOM */
  shadowRoot: ShadowRoot | null;
  /** The portal container element (typically .btcss-portal-container div), or null if not in a shadow DOM */
  portalContainer: HTMLElement | ShadowRoot | null;
}

/**
 * Shadow DOM Context
 * Used to provide shadow root and portal container information to child components
 */
const ShadowDOMContext = React.createContext<ShadowDOMContextValue>({
  shadowRoot: null,
  portalContainer: null,
});

/**
 * Shadow DOM Provider Props
 */
export interface ShadowDOMProviderProps {
  /** The ShadowRoot instance */
  shadowRoot: ShadowRoot | null;
  /** The portal container element where portals should render */
  portalContainer: HTMLElement | ShadowRoot | null;
  /** Child components */
  children: React.ReactNode;
}

/**
 * Shadow DOM Provider
 * Wraps components injected into Shadow DOM to provide shadow root and portal container context
 * 
 * @example
 * ```tsx
 * <ShadowDOMProvider shadowRoot={shadowRoot} portalContainer={portalContainer}>
 *   <YourComponent />
 * </ShadowDOMProvider>
 * ```
 */
export const ShadowDOMProvider: React.FC<ShadowDOMProviderProps> = ({
  shadowRoot,
  portalContainer,
  children,
}) => {
  // DEEP LOGGING: Track what values are being provided
  // React.useEffect(() => {
  //   console.log('[ShadowDOM] 🔍 PROVIDING context values:', {
  //     shadowRoot: shadowRoot ? {
  //       type: 'ShadowRoot',
  //       host: shadowRoot.host,
  //       hostId: (shadowRoot.host as HTMLElement)?.id,
  //       hostClassName: (shadowRoot.host as HTMLElement)?.className,
  //     } : null,
  //     portalContainer: portalContainer ? {
  //       type: portalContainer instanceof ShadowRoot ? 'ShadowRoot' : 'HTMLElement',
  //       tagName: portalContainer instanceof HTMLElement ? portalContainer.tagName : 'N/A',
  //       className: portalContainer instanceof HTMLElement ? portalContainer.className : 'N/A',
  //       isConnected: portalContainer instanceof HTMLElement ? portalContainer.isConnected : true,
  //       parentElement: portalContainer instanceof HTMLElement ? {
  //         tagName: portalContainer.parentElement?.tagName,
  //         className: portalContainer.parentElement?.className,
  //       } : null,
  //       isPortalContainer: portalContainer instanceof HTMLElement &&
  //         portalContainer.classList.contains('btcss-portal-container'),
  //       rootNode: portalContainer instanceof HTMLElement ?
  //         (portalContainer.getRootNode() instanceof ShadowRoot ? 'ShadowRoot' : 'Document') : 'ShadowRoot'
  //     } : null,
  //   });
  //
  //   // WARN if portalContainer is ShadowRoot instead of HTMLElement
  //   if (portalContainer instanceof ShadowRoot) {
  //     console.error('[ShadowDOM] ❌ ERROR: portalContainer is ShadowRoot, not HTMLElement!');
  //     console.error('[ShadowDOM] This should be the .btcss-portal-container div, not the shadow root');
  //   } else if (portalContainer && !portalContainer.classList.contains('btcss-portal-container')) {
  //     console.warn('[ShadowDOM] ⚠️ portalContainer is not .btcss-portal-container div:', {
  //       className: (portalContainer as HTMLElement).className,
  //       tagName: (portalContainer as HTMLElement).tagName,
  //     });
  //   }
  // }, [shadowRoot, portalContainer]);
  
  const value = React.useMemo<ShadowDOMContextValue>(
    () => ({
      shadowRoot,
      portalContainer,
    }),
    [shadowRoot, portalContainer]
  );

  return (
    <ShadowDOMContext.Provider value={value}>
      {children}
    </ShadowDOMContext.Provider>
  );
};

/**
 * Hook to access Shadow DOM context
 * 
 * @returns ShadowDOMContextValue with shadowRoot and portalContainer
 * 
 * @example
 * ```tsx
 * const { shadowRoot, portalContainer } = useShadowDOM();
 * // portalContainer will be null if not in a shadow DOM
 * ```
 */
export const useShadowDOM = (): ShadowDOMContextValue => {
  return React.useContext(ShadowDOMContext);
};

